import React from "react";
import { PortableText } from "@portabletext/react";
import { urlFor } from "lib/sanity";
import Link from "next/link";
import getYouTubeId from "get-youtube-id";
import ReactYouTube from "react-youtube";

export default function RichText({ portableText }) {
  return (
    <article className="richText">
      <PortableText value={portableText} components={components} />
    </article>
  );
}

const mediaComponents = {
  image({ alt, caption, image }) {
    return (
      <figure className="p-4 md:p-8">
        <img
          src={urlFor(image).auto("format").url()}
          alt={alt}
          className="mx-auto"
        />
        {caption && (
          <figcaption className="max-w-xl mx-auto mt-2 text-center text-gray-700">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  },
  youtube({ alt, caption, url }) {
    const id = getYouTubeId(url);
    return (
      <figure className="p-4 md:p-8">
        <ReactYouTube
          className="w-full max-w-2xl mx-auto"
          videoId={id}
          alt={alt}
        />
        {caption && (
          <figcaption className="max-w-xl mx-auto mt-2 text-center text-gray-700">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  },
  vimeo() {
    return <div>Vimeo</div>;
  },
};

const components = {
  types: {
    mediaReference: ({ value }) => {
      const Component = mediaComponents[value?.type];
      if (!Component) return null;
      return <Component {...value} />;
    },
    media: ({ value }) => {
      const Component = mediaComponents[value?.type];
      if (!Component) return null;
      return <Component {...value} />;
    },
  },
  marks: {
    caseLink: ({ value, text }) => {
      const { slug = "" } = value;
      return (
        <Link href={`/cases/${slug}`}>
          <a className="border-b-2 border-yellow-400 hover:border-yellow-500">
            {text}
          </a>
        </Link>
      );
    },
    ctaLink: ({ value, text }) => {
      const { href = "" } = value;
      return (
        <span className="pt-3">
          <Link href={href}>
            <a className="px-4 py-3 text-sm font-semibold text-white transition-all bg-black hover:bg-orange">
              {text}
            </a>
          </Link>
        </span>
      );
    },
    link: ({ value, children }) => {
      const target = (value?.href || "").startsWith("http")
        ? "_blank"
        : undefined;
      return (
        <a
          className="border-b-2 border-yellow-400 hover:border-yellow-500"
          href={value?.href}
          target={target}
          rel={target === "_blank" && "noindex nofollow"}
        >
          {children}
        </a>
      );
    },
  },
  block: {
    // Ex. 1: customizing common block types
    h1: ({ children }) => (
      <h1 style={{ fontSize: "2rem", fontWeight: 600, lineHeight: "3rem" }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="mt-3 text-xl font-semibold lg:mt-6 lg:text-2xl">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-3 text-lg font-semibold lg:mt-6 lg:text-xl">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mt-3 text-lg font-semibold lg:mt-6 lg:text-xl">
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5 className="mt-3 text-lg font-semibold lg:mt-6 lg:text-xl">
        {children}
      </h5>
    ),
    h6: ({ children }) => (
      <h6 className="mt-3 text-lg font-semibold lg:mt-6 lg:text-xl">
        {children}
      </h6>
    ),
    normal: ({ children }) => (
      <p className="mt-3 text-base leading-8 lg:mt-6 lg:text-lg">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="p-4 mt-3 text-base leading-8 border-l-4 bg-gray-50 lg:mt-6 lg:text-lg border-red">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="pl-4 mt-3 text-base leading-8 list-disc lg:mt-6 lg:text-lg">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="pl-4 mt-3 text-base leading-8 list-decimal lg:mt-6 lg:text-lg">
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="mt-1 lg:mt-2">{children}</li>,
    number: ({ children }) => <li className="mt-1 lg:mt-2">{children}</li>,
  },
};
